import React, { useState, useEffect, useMemo, useCallback } from "react";
import {
  Alert,
  Box,
  Typography,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip,
  TextField,
  Button,
} from "@mui/material";
import { Row, Col } from "react-bootstrap";
import { THEME_PRIMARY, THEME_PRIMARY_LIGHT, THEME_PRIMARY_TINT } from "../bookingTheme";
import { resolvePreviewSrc, pickVehicleCardImageFromApiField, readVehicleImagesJsonFromRow } from "../../../utils/mediaUtils";
import DefaultImage from "../../../assets/images/DefaultImage.png";
import { FileUploadInput } from "../../../components/forms";
import ImagePreviewCard from "../../../components/common/ImagePreviewCard";
import apiService from "../../../services/api";
import CurrentVehicleSummaryCard from "./SummaryCard";
import YourSelectionVehicleDetailRows from "./SelectionRows";
import {
  formatCurrentVehicleDetailValue,
  type CurrentBookingVehicleSummary,
} from "./summaryUtils";

export { formatCurrentVehicleDetailValue, type CurrentBookingVehicleSummary } from "./summaryUtils";

const BASE_IMAGE_URL = `${(process.env.REACT_APP_BASE_IMAGE_URL || "").replace(/\/?$/, "/")}`;
const resolvePreviewSrcForCard = (value: string): string => {
  const v = String(value || "").trim();
  if (!v) return DefaultImage;
  if (v.startsWith("http") || v.startsWith("data:")) return v;
  const cleaned = v.replace(/^\/+/, "");
  return cleaned ? `${BASE_IMAGE_URL}${cleaned}` : DefaultImage;
};

const parseImageUrls = (imageString: string | null | undefined): string[] => {
  if (!imageString || typeof imageString !== "string") return [];
  return imageString.split(",").map((u) => u.trim()).filter(Boolean);
};

function pickVehicleListRoot(response: unknown): unknown {
  if (response == null || typeof response !== "object" || Array.isArray(response)) return response;
  const o = response as Record<string, unknown>;
  if (Object.hasOwn(o, "data") && o.data !== undefined) return o.data;
  return response;
}

function parsePaginatedVehicleList(response: unknown): { items: any[]; total: number } {
  const root = pickVehicleListRoot(response);
  if (root == null) return { items: [], total: 0 };
  if (Array.isArray(root)) return { items: root, total: root.length };
  if (typeof root !== "object") return { items: [], total: 0 };
  const r = root as Record<string, unknown>;
  const itemsRaw =
    (Array.isArray(r.vehicles) && r.vehicles) ||
    (Array.isArray(r.result) && r.result) ||
    (Array.isArray(r.data) && r.data) ||
    (Array.isArray(r.items) && r.items) ||
    [];
  const items = itemsRaw;
  const totalCandidate =
    r.totalCount ?? r.totalRecords ?? r.total ?? r.totalRows ?? r.recordCount ?? r.totalItemCount;
  let total: number;
  if (typeof totalCandidate === "number" && Number.isFinite(totalCandidate)) {
    total = totalCandidate;
  } else if (typeof totalCandidate === "string" && totalCandidate.trim() !== "") {
    const p = Number.parseInt(totalCandidate, 10);
    total = Number.isFinite(p) ? p : items.length;
  } else {
    total = items.length;
  }
  return { items, total };
}

const BOOKING_VEHICLE_STATUS_OK = new Set([1, 5]);

function filterHubVehicleRowsForModel(rows: any[], modelIdStr: string): any[] {
  return rows.filter((row) => {
    const status = row.status ?? row.vehiclestatus ?? row.vehicleStatus;
    const num = status != null && status !== "" ? Number(status) : Number.NaN;
    if (!Number.isFinite(num) || !BOOKING_VEHICLE_STATUS_OK.has(num)) return false;
    const rowModel = row.vehiclemodel ?? row.vehiclemodelid;
    return String(rowModel) === modelIdStr;
  });
}

function mapApiVehicleRowToOption(
  v: any,
  getSpeedTypeDisplayName: (codeValue: string | number | null | undefined) => string
): VehicleOption {
  const speedTypeLabel = String(getSpeedTypeDisplayName(v.speedtype ?? v.speedType) ?? "").toLowerCase();
  return {
    id: v.vehicleid ?? v.id,
    name: v.vehiclename,
    speed: v.topspeed === null || v.topspeed === undefined ? "—" : `${v.topspeed} KM/H`,
    range: v.rangeperchargekm === null || v.rangeperchargekm === undefined ? "—" : `${v.rangeperchargekm} KM`,
    imageUrl: pickVehicleCardImageFromApiField(readVehicleImagesJsonFromRow(v)),
    vehicleNum: v.vehiclenum || "",
    registrationNumber:
      v.registrationnumber ?? v.registrationNumber ?? v.vechileistrationnumber ?? v.vehicleRegistrationNumber ?? "",
    vehicleType: v.vehicletype != null && v.vehicletype !== "" ? Number(v.vehicletype) : null,
    isHighSpeed: speedTypeLabel.includes("high"),
  };
}

export interface VehicleOption {
  id: string | number;
  name: string;
  speed: string;
  range: string;
  imageUrl?: string;
  vehicleNum?: string;
  registrationNumber?: string;
  /** From hub vehicle API (`vehicletype`): 1 = one-person, 2 = two-person */
  vehicleType?: number | null;
  /** True when EV speed type display name includes "high" (parent resolves via EVSpeedType codes). */
  isHighSpeed?: boolean;
}

export interface VehicleConditionChecklistItem {
  vehicleconditionchecklistid: number;
  checklistname: string;
  checklistdescription: string | null;
  checklistcode: string | null;
  createdby: number | null;
  modifiedby: number | null;
  checklisttype: number;
}

/** Trip start data from API for read-only display */
export interface TripStartData {
  tripmasterid?: number;
  vehiclephotos?: string | null;
  batteryphotos?: string | null;
  chargerphotos?: string | null;
  e_signature?: string | null;
  vehicleconditionchecklistmappings?: Array<{
    vehicleconditionchecklistid?: number;
    checklistname?: string;
    vehicleconditioncheckedstatus?: boolean;
  }>;
}

interface SelectVehicleStepProps {
  vehicles: VehicleOption[];
  selectedId: string | number | null;
  /** May be async (e.g. ChangeVehicleStatusToUnderbooking before committing selection). */
  onSelect: (id: string | number) => void | Promise<void>;
  loading?: boolean;
  vehiclePhotos: string[];
  batteryPhotos: string[];
  chargerPhotos: string[];
  onVehiclePhotosChange: (addedUrls: string[]) => void;
  onBatteryPhotosChange: (addedUrls: string[]) => void;
  onChargerPhotosChange: (addedUrls: string[]) => void;
  onVehiclePhotoDelete: (index: number) => void;
  onBatteryPhotoDelete: (index: number) => void;
  onChargerPhotoDelete: (index: number) => void;
  checklistMappings: Record<string, boolean>;
  onChecklistChange: (mappings: Record<string, boolean>) => void;
  /** When true, show validation state (red border, error) on vehicle upload fields if empty */
  showVehicleUploadValidation?: boolean;
  tripStatus?: number | null;
  /** Trip start from API - used for read-only display */
  tripStart?: TripStartData | null;
  /** Original vehicle id from booking - when different from selectedId, show new vehicle section */
  originalVehicleId?: number | null;
  /** When true, old vehicle checklist/attachments are editable (after "Update Checklist" click) */
  isEditingOldChecklist?: boolean;
  onEditingOldChecklistChange?: (editing: boolean) => void;
  /** New vehicle section (when user selected a different vehicle) */
  newVehiclePhotos?: string[];
  newVehicleBatteryPhotos?: string[];
  newVehicleChargerPhotos?: string[];
  newVehicleChecklistMappings?: Record<string, boolean>;
  onNewVehiclePhotosChange?: (urls: string[]) => void;
  onNewVehicleBatteryPhotosChange?: (urls: string[]) => void;
  onNewVehicleChargerPhotosChange?: (urls: string[]) => void;
  onNewVehiclePhotoDelete?: (index: number) => void;
  onNewVehicleBatteryPhotoDelete?: (index: number) => void;
  onNewVehicleChargerPhotoDelete?: (index: number) => void;
  onNewVehicleChecklistChange?: (mappings: Record<string, boolean>) => void;
  originalVehicleType?: number | null;
  selectedVehicleType?: number | null;
  selectedVehicleModelName?: string | null;
  /** Hub chosen in step 0 — shown above selected model summary */
  selectedHubName?: string | null;
  currentBookingVehicle?: CurrentBookingVehicleSummary | null;
  /** When KYC is not complete (kycstatus !== 1), vehicles with vehicleType === 1 cannot be selected */
  kycBlocksOnePersonVehicles?: boolean;
  /** When KYC is not verified or DL front OCR image is missing, high-speed vehicles cannot be selected */
  kycBlocksHighSpeedVehicles?: boolean;
  /** While true, do not apply KYC gate (avoid flicker / wrong lock before API returns) */
  kycCheckLoading?: boolean;
  kycIncompleteMessage?: string;
  kycHighSpeedMessage?: string;
  vehicleListSearch?: {
    hubMasterId: number;
    vehicleModelId: string | number;
    getSpeedTypeDisplayName: (codeValue: string | number | null | undefined) => string;
  };
  onRefreshVehicles?: () => void | Promise<void>;
}

function useVehicleListSearch(
  vehicleListSearch: SelectVehicleStepProps["vehicleListSearch"],
  vehicles: VehicleOption[]
) {
  const [vehicleNumSearch, setVehicleNumSearch] = useState("");
  const [registrationSearch, setRegistrationSearch] = useState("");
  const [vehicleSearchLoading, setVehicleSearchLoading] = useState(false);
  const [vehicleSearchError, setVehicleSearchError] = useState<string | null>(null);
  const [searchResultVehicles, setSearchResultVehicles] = useState<VehicleOption[] | null>(null);

  useEffect(() => {
    setSearchResultVehicles(null);
    setVehicleSearchError(null);
    setVehicleNumSearch("");
    setRegistrationSearch("");
  }, [vehicleListSearch?.hubMasterId, vehicleListSearch?.vehicleModelId]);

  const displayedVehicles = searchResultVehicles ?? vehicles;

  const runVehicleListSearch = useCallback(async () => {
    if (!vehicleListSearch) return;
    const vn = vehicleNumSearch.trim();
    const reg = registrationSearch.trim();
    if (vn === "" && reg === "") {
      setVehicleSearchError("Enter a vehicle number and/or registration number to search.");
      return;
    }
    setVehicleSearchError(null);
    setVehicleSearchLoading(true);
    try {
      const modelNum = Number(vehicleListSearch.vehicleModelId);
      const payload = {
        pageNumber: 1,
        pageSize: 100,
        vehiclenum: vn,
        registrationnumber: reg,
        telematicsid: "",
        vehiclemodel: Number.isFinite(modelNum) ? modelNum : 0,
        hubmasterid: vehicleListSearch.hubMasterId,
        status: 0,
      };
      const response = await apiService.getAllVehicles(payload);
      const { items } = parsePaginatedVehicleList(response);
      const modelIdStr = String(vehicleListSearch.vehicleModelId);
      const filtered = filterHubVehicleRowsForModel(Array.isArray(items) ? items : [], modelIdStr);
      const mapped = filtered.map((row) =>
        mapApiVehicleRowToOption(row, vehicleListSearch.getSpeedTypeDisplayName)
      );
      setSearchResultVehicles(mapped);
    } catch (e: unknown) {
      const msg = e instanceof Error && e.message ? e.message : "Vehicle search failed.";
      setVehicleSearchError(msg);
      setSearchResultVehicles(null);
    } finally {
      setVehicleSearchLoading(false);
    }
  }, [vehicleListSearch, vehicleNumSearch, registrationSearch]);

  const clearVehicleSearch = useCallback(() => {
    setVehicleNumSearch("");
    setRegistrationSearch("");
    setSearchResultVehicles(null);
    setVehicleSearchError(null);
  }, []);

  return {
    vehicleNumSearch,
    setVehicleNumSearch,
    registrationSearch,
    setRegistrationSearch,
    vehicleSearchLoading,
    vehicleSearchError,
    setVehicleSearchError,
    displayedVehicles,
    runVehicleListSearch,
    clearVehicleSearch,
    searchResultVehicles,
  };
}

const SelectVehicleStep: React.FC<SelectVehicleStepProps> = ({
  vehicles,
  selectedId,
  onSelect,
  loading = false,
  vehiclePhotos,
  batteryPhotos,
  chargerPhotos,
  onVehiclePhotosChange,
  onBatteryPhotosChange,
  onChargerPhotosChange,
  onVehiclePhotoDelete,
  onBatteryPhotoDelete,
  onChargerPhotoDelete,
  checklistMappings,
  onChecklistChange,
  showVehicleUploadValidation = false,
  tripStatus,
  tripStart,
  originalVehicleId = null,
  isEditingOldChecklist = false,
  onEditingOldChecklistChange,
  newVehiclePhotos = [],
  newVehicleBatteryPhotos = [],
  newVehicleChargerPhotos = [],
  newVehicleChecklistMappings = {},
  onNewVehiclePhotosChange,
  onNewVehicleBatteryPhotosChange,
  onNewVehicleChargerPhotosChange,
  onNewVehiclePhotoDelete,
  onNewVehicleBatteryPhotoDelete,
  onNewVehicleChargerPhotoDelete,
  onNewVehicleChecklistChange,
  originalVehicleType = null,
  selectedVehicleType = null,
  selectedVehicleModelName,
  selectedHubName,
  currentBookingVehicle = null,
  kycBlocksOnePersonVehicles = false,
  kycBlocksHighSpeedVehicles = false,
  kycCheckLoading = false,
  kycIncompleteMessage = "Your KYC is not completed.",
  kycHighSpeedMessage = "Driving licence verification is required for high-speed vehicles.",
  vehicleListSearch,
  onRefreshVehicles,
}) => {
  const modelLabel = selectedVehicleModelName?.trim() || "";
  const hubLabel = selectedHubName?.trim() || "";

  const {
    vehicleNumSearch,
    setVehicleNumSearch,
    registrationSearch,
    setRegistrationSearch,
    vehicleSearchLoading,
    vehicleSearchError,
    setVehicleSearchError,
    displayedVehicles,
    runVehicleListSearch,
    clearVehicleSearch,
    searchResultVehicles,
  } = useVehicleListSearch(vehicleListSearch, vehicles);

  const handleRefreshVehicles = useCallback(async () => {
    clearVehicleSearch();
    if (onRefreshVehicles) await onRefreshVehicles();
  }, [clearVehicleSearch, onRefreshVehicles]);

  const yourSelectionVehicleDetails = useMemo(() => {
    const v = displayedVehicles.find((x) => selectedId != null && String(x.id) === String(selectedId));
    return {
      vehicleName: String(v?.name ?? "").trim(),
      vehicleNum: String(v?.vehicleNum ?? "").trim(),
      registrationNumber: String(v?.registrationNumber ?? "").trim(),
      speed: String(v?.speed ?? "").trim(),
      range: String(v?.range ?? "").trim(),
    };
  }, [displayedVehicles, selectedId]);

  const hasOnePersonInList = displayedVehicles.some((v) => Number(v.vehicleType) === 1);
  const hasHighSpeedInList = displayedVehicles.some((v) => v.isHighSpeed === true);
  const showKycOnePersonWarning = !kycCheckLoading && kycBlocksOnePersonVehicles && hasOnePersonInList;
  const showKycHighSpeedWarning = !kycCheckLoading && kycBlocksHighSpeedVehicles && hasHighSpeedInList;
  const showKycVehicleWarning = showKycOnePersonWarning || showKycHighSpeedWarning;

  const [checklistItems, setChecklistItems] = useState<VehicleConditionChecklistItem[]>([]);
  const [checklistLoading, setChecklistLoading] = useState(false);

  const hasTripStart = tripStart && (tripStart.vehicleconditionchecklistmappings?.length || tripStart.vehiclephotos || tripStart.batteryphotos || tripStart.chargerphotos);
  const isNewVehicleSection = originalVehicleId != null && selectedId != null && Number(selectedId) !== Number(originalVehicleId);

  const checklistUploadValidationActive = tripStatus === 5;
  const isBatteryChargerRequiredUpdate = checklistUploadValidationActive && originalVehicleType === 1;
  const isBatteryChargerRequiredNew = checklistUploadValidationActive && selectedVehicleType === 1;
  const hasUpdateChecklistSelection = Object.values(checklistMappings).includes(true);
  const hasNewChecklistSelection = Object.values(newVehicleChecklistMappings).includes(true);

  useEffect(() => {
    const needChecklist = (tripStatus === 5 && hasTripStart) || isNewVehicleSection;
    if (!needChecklist) {
      setChecklistItems([]);
      setChecklistLoading(false);
      return () => {};
    }
    let cancelled = false;
    setChecklistLoading(true);
    apiService.getVehicleConditionCheckList(1).then((res: any) => {
      const data = res?.data;
      if (!cancelled && Array.isArray(data)) setChecklistItems(data);
    }).catch(() => {
      if (!cancelled) setChecklistItems([]);
    }).finally(() => {
      if (!cancelled) setChecklistLoading(false);
    });
    return () => { cancelled = true; };
  }, [tripStatus, hasTripStart, isNewVehicleSection]);

  useEffect(() => {
    if (!isNewVehicleSection || checklistItems.length === 0 || !onNewVehicleChecklistChange) return;
    const allIds = checklistItems.map((item) => String(item.vehicleconditionchecklistid));
    const missingIds = allIds.filter((id) => !(id in newVehicleChecklistMappings));
    if (missingIds.length === 0) return;
    const withDefaults = Object.fromEntries(missingIds.map((id) => [id, false]));
    onNewVehicleChecklistChange({ ...newVehicleChecklistMappings, ...withDefaults });
  }, [isNewVehicleSection, checklistItems, newVehicleChecklistMappings, onNewVehicleChecklistChange]);

  const handleChecklistToggle = (id: number, checked: boolean) => {
    onChecklistChange({ ...checklistMappings, [String(id)]: checked });
  };

  const handleNewChecklistToggle = (id: number, checked: boolean) => {
    onNewVehicleChecklistChange?.({ ...newVehicleChecklistMappings, [String(id)]: checked });
  };

  const formData = {
    vehicleimages: vehiclePhotos,
    batteryimages: batteryPhotos,
    chargerimages: chargerPhotos,
  };
  const newVehicleFormData = {
    vehicleimages: newVehiclePhotos,
    batteryimages: newVehicleBatteryPhotos,
    chargerimages: newVehicleChargerPhotos,
  };

  const handleInputChange = (field: string, value: string[] | string) => {
    const newUrls = Array.isArray(value) ? value : value ? [value] : [];
    if (field === "vehicleimages") onVehiclePhotosChange(newUrls);
    else if (field === "batteryimages") onBatteryPhotosChange(newUrls);
    else if (field === "chargerimages") onChargerPhotosChange(newUrls);
  };

  const handleNewVehicleInputChange = (field: string, value: string[] | string) => {
    const newUrls = Array.isArray(value) ? value : value ? [value] : [];
    if (field === "vehicleimages") onNewVehiclePhotosChange?.(newUrls);
    else if (field === "batteryimages") onNewVehicleBatteryPhotosChange?.(newUrls);
    else if (field === "chargerimages") onNewVehicleChargerPhotosChange?.(newUrls);
  };

  const selectionSummaryRow = (label: string, value: string) => (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "minmax(108px, 38%) minmax(0, 1fr)", sm: "118px minmax(0, 1fr)" },
        columnGap: { xs: 1.25, sm: 1.5 },
        alignItems: "center",
        py: { xs: 0.75, sm: 0.625 },
        borderBottom: "1px solid rgba(22, 67, 135, 0.1)",
        "&:last-of-type": { borderBottom: "none", pb: 0 },
        "&:first-of-type": { pt: 0 },
      }}
    >
      <Typography
        variant="body2"
        sx={{
          fontWeight: 600,
          color: "text.secondary",
          fontSize: { xs: "0.8125rem", sm: "0.875rem" },
          lineHeight: 1.35,
        }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: "0.875rem", sm: "0.9375rem" },
          lineHeight: 1.35,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          minWidth: 0,
        }}
        title={value}
      >
        {value}
      </Typography>
    </Box>
  );

  const summaryCardSx = {
    border: "1px solid rgba(22, 67, 135, 0.2)",
    borderRadius: 2,
    bgcolor: THEME_PRIMARY_TINT,
    px: { xs: 1.5, sm: 1.75 },
    py: { xs: 1.25, sm: 1.125 },
    boxShadow: "0 1px 2px rgba(22, 67, 135, 0.06)",
    minWidth: 0,
    width: "100%",
  } as const;

  const summaryCardTitleSx = {
    display: "block",
    fontWeight: 700,
    color: THEME_PRIMARY,
    fontSize: "0.7rem",
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    mb: 0.75,
    pb: 0.5,
    borderBottom: `2px solid ${THEME_PRIMARY}`,
  } as const;

  const showYourSelection = Boolean(hubLabel || modelLabel);
  const showCurrentVehicle = Boolean(currentBookingVehicle);
  const showSummaryRow = showYourSelection || showCurrentVehicle;

  return (
    <Box sx={{ mb: 1, minWidth: 0 }}>
      

      {showSummaryRow ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "1fr",
              sm: showYourSelection && showCurrentVehicle ? "repeat(2, minmax(0, 1fr))" : "1fr",
            },
            gap: { xs: 1.75, sm: 2 },
            width: "100%",
            maxWidth: { sm: showYourSelection && showCurrentVehicle ? 920 : 480 },
            mb: { xs: 2, sm: 2.5, md: 3 },
            alignItems: "stretch",
          }}
        >
          {showCurrentVehicle && currentBookingVehicle ? (
            <CurrentVehicleSummaryCard vehicle={currentBookingVehicle} />
          ) : null}
          {showYourSelection ? (
            <Box sx={summaryCardSx}>
              <Typography variant="caption" sx={summaryCardTitleSx}>
                Your selection
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
                {selectionSummaryRow("Hub", formatCurrentVehicleDetailValue(hubLabel))}
                {selectionSummaryRow("Model", formatCurrentVehicleDetailValue(modelLabel))}
                <YourSelectionVehicleDetailRows
                  selectionSummaryRow={selectionSummaryRow}
                  details={yourSelectionVehicleDetails}
                />
              </Box>
            </Box>
          ) : null}
        </Box>
      ) : null}

      <Typography
        variant="h6"
        sx={{
          fontWeight: 700,
          color: "text.primary",
          fontSize: { xs: 18, sm: 20 },
          lineHeight: 1.35,
          width: "100%",
          mb: showSummaryRow ? { xs: 1.5, sm: 1.75 } : { xs: 2, sm: 2.5 },
        }}
      >
        Please Select Vehicle
      </Typography>

      {vehicleListSearch || onRefreshVehicles ? (
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", sm: "row" },
            flexWrap: "wrap",
            alignItems: { xs: "stretch", sm: "flex-end" },
            gap: { xs: 1.25, sm: 1.5 },
            width: "100%",
            maxWidth: 900,
            mb: { xs: 2, sm: 2.25 },
          }}
        >
          {vehicleListSearch ? (
            <>
              <TextField
                label="Vehicle number"
                size="small"
                value={vehicleNumSearch}
                onChange={(e) => {
                  setVehicleNumSearch(e.target.value);
                  if (vehicleSearchError) setVehicleSearchError(null);
                }}
                sx={{ flex: { xs: "1 1 auto", sm: "1 1 160px" }, minWidth: { sm: 160 } }}
              />
              <TextField
                label="Registration number"
                size="small"
                value={registrationSearch}
                onChange={(e) => {
                  setRegistrationSearch(e.target.value);
                  if (vehicleSearchError) setVehicleSearchError(null);
                }}
                sx={{ flex: { xs: "1 1 auto", sm: "1 1 180px" }, minWidth: { sm: 180 } }}
              />
              <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap", alignItems: "center" }}>
                <Button
                  type="button"
                  variant="contained"
                  onClick={() => void runVehicleListSearch()}
                  disabled={vehicleSearchLoading || loading}
                  sx={{
                    bgcolor: "#FFDE00",
                    color: "#111",
                    textTransform: "none",
                    fontWeight: 700,
                    boxShadow: "none",
                    "&:hover": { bgcolor: "#F3CF00", boxShadow: "none" },
                  }}
                >
                  Search
                </Button>
                <Button
                  type="button"
                  variant="text"
                  onClick={clearVehicleSearch}
                  disabled={
                    vehicleSearchLoading ||
                    loading ||
                    (searchResultVehicles === null &&
                      vehicleNumSearch.trim() === "" &&
                      registrationSearch.trim() === "")
                  }
                  sx={{ color: THEME_PRIMARY, textTransform: "none", fontWeight: 600 }}
                >
                  Clear search
                </Button>
              </Box>
            </>
          ) : null}
          {onRefreshVehicles ? (
            <Button
              type="button"
              variant="outlined"
              onClick={() => void handleRefreshVehicles()}
              disabled={loading || vehicleSearchLoading}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderColor: THEME_PRIMARY,
                color: THEME_PRIMARY,
                alignSelf: { xs: "flex-end", sm: "flex-end" },
                marginLeft: { xs: 0, sm: "auto" },
              }}
            >
              Refresh vehicles
            </Button>
          ) : null}
        </Box>
      ) : null}

      {vehicleSearchError ? (
        <Alert severity="error" sx={{ mb: 2, maxWidth: 900 }} onClose={() => setVehicleSearchError(null)}>
          {vehicleSearchError}
        </Alert>
      ) : null}

      {(() => {
        if (loading || vehicleSearchLoading) {
          return <Typography color="text.secondary" sx={{ py: 2 }}>Loading vehicles...</Typography>;
        }
        if (displayedVehicles.length === 0) {
          let emptyMsg = "No vehicles match your search for this hub and model.";
          if (searchResultVehicles === null) {
            emptyMsg = "No vehicles available for this hub.";
          }
          return <Typography color="text.secondary" sx={{ py: 2 }}>{emptyMsg}</Typography>;
        }
        return (
          <Box sx={{ mb: { xs: 2, sm: 3 } }}>
            {showKycVehicleWarning && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                {showKycOnePersonWarning && (
                  <Typography variant="body2" component="div" sx={{ mb: showKycHighSpeedWarning ? 1.25 : 0 }}>
                    {kycIncompleteMessage}
                  </Typography>
                )}
                {showKycHighSpeedWarning && (
                  <Typography variant="body2" component="div">
                    {kycHighSpeedMessage}
                  </Typography>
                )}
              </Alert>
            )}
            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(3, 1fr)" },
                gap: { xs: 1.5, sm: 2 },
                alignItems: "stretch",
              }}
            >
            {displayedVehicles.map((vehicle) => {
              const isSelected = selectedId !== null && String(selectedId) === String(vehicle.id);
              const isOnePersonVehicle = Number(vehicle.vehicleType) === 1;
              const isHighSpeedVehicle = vehicle.isHighSpeed === true;
              const selectionDisabled =
                !kycCheckLoading &&
                ((kycBlocksOnePersonVehicles && isOnePersonVehicle) ||
                  (kycBlocksHighSpeedVehicles && isHighSpeedVehicle));
              return (
                <Card
                  key={vehicle.id}
                  onClick={() => {
                    if (selectionDisabled) return;
                    void onSelect(vehicle.id);
                  }}
                  sx={{
                    cursor: selectionDisabled ? "not-allowed" : "pointer",
                    border: "1px solid",
                    borderColor: isSelected ? THEME_PRIMARY_LIGHT : "rgba(22, 67, 135, 0.25)",
                    borderRadius: "10px",
                    bgcolor: isSelected ? "#E8E8FF" : "#fff",
                    boxShadow: "none",
                    overflow: "hidden",
                    display: "flex",
                    flexDirection: "column",
                    minHeight: { xs: 130, sm: 160 },
                    opacity: selectionDisabled ? 0.55 : 1,
                    pointerEvents: selectionDisabled ? "none" : "auto",
                    "&:hover": selectionDisabled
                      ? {}
                      : { borderColor: THEME_PRIMARY_LIGHT, boxShadow: "0 2px 8px rgba(0,0,0,0.08)" },
                  }}
                >
                  <CardContent sx={{ p: 0, flex: 1, display: "flex", flexDirection: "column", "&:last-child": { pb: 0 } }}>
                    <Box
                      sx={{
                        display: "flex",
                        flex: 1,
                        minHeight: 0,
                        alignItems: "center",
                        gap: { xs: 1.5, sm: 2 },
                        px: { xs: 1.5, sm: 2 },
                        py: { xs: 1.25, sm: 1.5 },
                      }}
                    >
                      <Box sx={{ flex: "2 1 0", minWidth: 0, display: "flex", flexDirection: "column", justifyContent: "center" }}>
                        <Typography sx={{ fontWeight: 700, color: THEME_PRIMARY, fontSize: "1em", mb: 1.25, lineHeight: 1.4 }}>
                          {vehicle.name}
                        </Typography>
                        <Typography sx={{ color: "#666666", fontSize: "0.9375rem", fontWeight: 550, display: "block", lineHeight: 1.6 }}>
                          Vehicle No.: {formatCurrentVehicleDetailValue(vehicle.vehicleNum?.trim() ? vehicle.vehicleNum.trim() : "")}
                        </Typography>
                        <Typography sx={{ color: "#666666", fontSize: "0.9375rem", fontWeight: 550, display: "block", lineHeight: 1.6 }}>
                          Registration No.: {formatCurrentVehicleDetailValue(vehicle.registrationNumber?.trim() ? vehicle.registrationNumber.trim() : "")}
                        </Typography>
                        <Typography sx={{ color: "#666666", fontSize: "1rem", fontWeight: 550, display: "block", lineHeight: 1.6 }}>
                          Speed: {vehicle.speed}
                        </Typography>
                        <Typography sx={{ color: "#666666", fontSize: "1rem", fontWeight: 550, display: "block", lineHeight: 1.6 }}>
                          Range: {vehicle.range}
                        </Typography>
                      </Box>
                      <Box sx={{ flex: "1 1 0", maxWidth: "33%", display: "flex", alignItems: "center", justifyContent: "center", alignSelf: "stretch" }}>
                        <Box sx={{ width: "100%", height: "100%", minHeight: 72, maxHeight: 100, borderRadius: "10px", overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                          <Box
                            component="img"
                            key={`veh-thumb-${String(vehicle.id)}`}
                            src={resolvePreviewSrc(vehicle.imageUrl, DefaultImage)}
                            alt={vehicle.name}
                            onError={(e: React.SyntheticEvent<HTMLImageElement>) => { e.currentTarget.src = DefaultImage; }}
                            sx={{
                              width: "100%",
                              height: "100%",
                              minHeight: 72,
                              maxHeight: 100,
                              objectFit: "contain",
                              display: "block",
                            }}
                          />
                        </Box>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              );
            })}
            </Box>
          </Box>
        );
      })()}

      {/* Read-only: Trip Start – same alignment as Update existing vehicle checklist */}
      {hasTripStart && (
        <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
            Trip Start
          </Typography>
          {tripStart?.vehicleconditionchecklistmappings && tripStart.vehicleconditionchecklistmappings.length > 0 && (
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(auto-fill, minmax(180px, 1fr))" }, gap: 0.5, mb: 2 }}>
              {tripStart.vehicleconditionchecklistmappings.map((item: any, idx: number) => {
                const checked = item.vehicleconditioncheckedstatus === true;
                return (
                  <Box key={item.vehicleconditionchecklistid ?? idx} sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <Chip
                      label={checked ? "✓" : "✗"}
                      size="small"
                      sx={{
                        backgroundColor: checked ? "#D4FFC7" : "#FFE3E3",
                        color: checked ? "#1F710D" : "#C50000",
                        fontWeight: "bold",
                        minWidth: 30,
                      }}
                    />
                    <Typography variant="body2" sx={{ fontSize: "0.875rem" }}>{item.checklistname || "N/A"}</Typography>
                  </Box>
                );
              })}
            </Box>
          )}
          <Typography variant="body2" sx={{ color: THEME_PRIMARY, fontWeight: 600, mb: 1 }}>Attachments</Typography>
          <Row className="g-3">
            <Col xs={12} md={4}>
              {parseImageUrls(tripStart?.vehiclephotos).length > 0 && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {parseImageUrls(tripStart?.vehiclephotos).map((url, idx) => (
                    <ImagePreviewCard key={`ro-vehicle-${idx}`} title={`Vehicle ${idx + 1}`} value={url} resolveSrc={resolvePreviewSrcForCard} size={200} defaultImage={DefaultImage} />
                  ))}
                </Box>
              )}
            </Col>
            <Col xs={12} md={4}>
              {parseImageUrls(tripStart?.batteryphotos).length > 0 && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {parseImageUrls(tripStart?.batteryphotos).map((url, idx) => (
                    <ImagePreviewCard key={`ro-battery-${idx}`} title={`Battery ${idx + 1}`} value={url} resolveSrc={resolvePreviewSrcForCard} size={200} defaultImage={DefaultImage} />
                  ))}
                </Box>
              )}
            </Col>
            <Col xs={12} md={4}>
              {parseImageUrls(tripStart?.chargerphotos).length > 0 && (
                <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                  {parseImageUrls(tripStart?.chargerphotos).map((url, idx) => (
                    <ImagePreviewCard key={`ro-charger-${idx}`} title={`Charger ${idx + 1}`} value={url} resolveSrc={resolvePreviewSrcForCard} size={200} defaultImage={DefaultImage} />
                  ))}
                </Box>
              )}
            </Col>
          </Row>
        </Box>
      )}

      {tripStatus === 5 && hasTripStart && (
        <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
            Update existing vehicle checklist
          </Typography>
          {checklistLoading ? (
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>Loading checklist...</Typography>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(auto-fill, minmax(180px, 1fr))" }, gap: 0.5, mb: 1 }}>
                {checklistItems.map((item) => (
                  <FormControlLabel
                    key={item.vehicleconditionchecklistid}
                    control={
                      <Checkbox
                        checked={!!checklistMappings[String(item.vehicleconditionchecklistid)]}
                        onChange={(_, checked) => handleChecklistToggle(item.vehicleconditionchecklistid, checked)}
                        size="small"
                        sx={{ color: THEME_PRIMARY, "&.Mui-checked": { color: THEME_PRIMARY } }}
                      />
                    }
                    label={item.checklistname}
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                  />
                ))}
              </Box>
              {checklistUploadValidationActive && showVehicleUploadValidation && checklistItems.length > 0 && !hasUpdateChecklistSelection && (
                <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mb: 2 }}>
                  Select at least one checklist item.
                </Typography>
              )}
            </>
          )}
          <Typography variant="body2" sx={{ color: THEME_PRIMARY, fontWeight: 600, mb: 1 }}>Attachments</Typography>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <FileUploadInput
                field="vehicleimages"
                formData={formData}
                onChange={handleInputChange}
                label="Upload Vehicle Images"
                folderPath="vehicle"
                folderType="vehicle"
                multiple
                maxFiles={5}
                required={checklistUploadValidationActive}
                error={checklistUploadValidationActive && showVehicleUploadValidation && (vehiclePhotos?.length ?? 0) === 0 ? "Vehicle images are required" : undefined}
              />
            </Col>
            {originalVehicleType !== 2 && (
              <>
                <Col xs={12} md={4}>
                  <FileUploadInput
                    field="batteryimages"
                    formData={formData}
                    onChange={handleInputChange}
                    label="Upload Battery Images"
                    folderPath="battery"
                    folderType="battery"
                    multiple
                    maxFiles={2}
                    required={isBatteryChargerRequiredUpdate}
                    error={checklistUploadValidationActive && showVehicleUploadValidation && isBatteryChargerRequiredUpdate && (batteryPhotos?.length ?? 0) === 0 ? "Battery images are required" : undefined}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FileUploadInput
                    field="chargerimages"
                    formData={formData}
                    onChange={handleInputChange}
                    label="Upload Charger Images"
                    folderPath="charger"
                    folderType="charger"
                    multiple
                    maxFiles={2}
                    required={isBatteryChargerRequiredUpdate}
                    error={checklistUploadValidationActive && showVehicleUploadValidation && isBatteryChargerRequiredUpdate && (chargerPhotos?.length ?? 0) === 0 ? "Charger images are required" : undefined}
                  />
                </Col>
              </>
            )}
          </Row>
          {((vehiclePhotos?.length ?? 0) > 0 || (batteryPhotos?.length ?? 0) > 0 || (chargerPhotos?.length ?? 0) > 0) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Uploaded Images</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {(vehiclePhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`old-v-${index}`} title={`Vehicle ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onVehiclePhotoDelete(index)} size={200} defaultImage={DefaultImage} />
                ))}
                {(batteryPhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`old-b-${index}`} title={`Battery ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onBatteryPhotoDelete(index)} size={200} defaultImage={DefaultImage} />
                ))}
                {(chargerPhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`old-c-${index}`} title={`Charger ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onChargerPhotoDelete(index)} size={200} defaultImage={DefaultImage} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}

      {/* New vehicle section (when user selected a different vehicle; only when tripstatus === 5) */}
      {isNewVehicleSection && tripStatus === 5 && (
        <Box sx={{ mt: 3, p: 2, border: "1px solid #e0e0e0", borderRadius: 2, bgcolor: "rgba(22, 67, 135, 0.04)" }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1.5, color: "text.primary" }}>
            New vehicle checklist
          </Typography>
          {checklistLoading ? (
            <Typography color="text.secondary" variant="body2" sx={{ mb: 2 }}>Loading checklist...</Typography>
          ) : (
            <>
              <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(auto-fill, minmax(180px, 1fr))" }, gap: 0.5, mb: 1 }}>
                {checklistItems.map((item) => (
                  <FormControlLabel
                    key={item.vehicleconditionchecklistid}
                    control={
                      <Checkbox
                        checked={!!newVehicleChecklistMappings[String(item.vehicleconditionchecklistid)]}
                        onChange={(_, checked) => handleNewChecklistToggle(item.vehicleconditionchecklistid, checked)}
                        size="small"
                        sx={{ color: THEME_PRIMARY, "&.Mui-checked": { color: THEME_PRIMARY } }}
                      />
                    }
                    label={item.checklistname}
                    sx={{ "& .MuiFormControlLabel-label": { fontSize: "0.875rem" } }}
                  />
                ))}
              </Box>
              {checklistUploadValidationActive && showVehicleUploadValidation && checklistItems.length > 0 && !hasNewChecklistSelection && (
                <Typography variant="caption" sx={{ color: "#d32f2f", display: "block", mb: 2 }}>
                  Select at least one checklist item.
                </Typography>
              )}
            </>
          )}
          <Typography variant="body2" sx={{ color: THEME_PRIMARY, fontWeight: 600, mb: 1 }}>Attachments</Typography>
          <Row className="g-3">
            <Col xs={12} md={4}>
              <FileUploadInput
                field="vehicleimages"
                formData={newVehicleFormData}
                onChange={handleNewVehicleInputChange}
                label="Upload Vehicle Images"
                folderPath="vehicle"
                folderType="vehicle"
                multiple
                maxFiles={Math.max(1, 5 - (newVehiclePhotos?.length ?? 0))}
                required={checklistUploadValidationActive}
                error={checklistUploadValidationActive && showVehicleUploadValidation && (newVehiclePhotos?.length ?? 0) === 0 ? "Vehicle images are required" : undefined}
              />
            </Col>
            {selectedVehicleType !== 2 && (
              <>
                <Col xs={12} md={4}>
                  <FileUploadInput
                    field="batteryimages"
                    formData={newVehicleFormData}
                    onChange={handleNewVehicleInputChange}
                    label="Upload Battery Images"
                    folderPath="battery"
                    folderType="battery"
                    multiple
                    maxFiles={Math.max(1, 3 - (newVehicleBatteryPhotos?.length ?? 0))}
                    required={isBatteryChargerRequiredNew}
                    error={checklistUploadValidationActive && showVehicleUploadValidation && isBatteryChargerRequiredNew && (newVehicleBatteryPhotos?.length ?? 0) === 0 ? "Battery images are required" : undefined}
                  />
                </Col>
                <Col xs={12} md={4}>
                  <FileUploadInput
                    field="chargerimages"
                    formData={newVehicleFormData}
                    onChange={handleNewVehicleInputChange}
                    label="Upload Charger Images"
                    folderPath="charger"
                    folderType="charger"
                    multiple
                    maxFiles={Math.max(1, 3 - (newVehicleChargerPhotos?.length ?? 0))}
                    required={isBatteryChargerRequiredNew}
                    error={checklistUploadValidationActive && showVehicleUploadValidation && isBatteryChargerRequiredNew && (newVehicleChargerPhotos?.length ?? 0) === 0 ? "Charger images are required" : undefined}
                  />
                </Col>
              </>
            )}
          </Row>
          {((newVehiclePhotos?.length ?? 0) > 0 || (newVehicleBatteryPhotos?.length ?? 0) > 0 || (newVehicleChargerPhotos?.length ?? 0) > 0) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>Uploaded Images</Typography>
              <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
                {(newVehiclePhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`new-v-${index}`} title={`Vehicle ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onNewVehiclePhotoDelete?.(index)} size={200} defaultImage={DefaultImage} />
                ))}
                {(newVehicleBatteryPhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`new-b-${index}`} title={`Battery ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onNewVehicleBatteryPhotoDelete?.(index)} size={200} defaultImage={DefaultImage} />
                ))}
                {(newVehicleChargerPhotos ?? []).map((image, index) => (
                  <ImagePreviewCard key={`new-c-${index}`} title={`Charger ${index + 1}`} value={image} resolveSrc={resolvePreviewSrcForCard} onDelete={() => onNewVehicleChargerPhotoDelete?.(index)} size={200} defaultImage={DefaultImage} />
                ))}
              </Box>
            </Box>
          )}
        </Box>
      )}
    </Box>
  );
};

export default SelectVehicleStep;
